import { useCallback } from 'react';
import noop from 'lodash/noop';
import type {
	AccessRole,
	ViewAccessLevel,
} from '@jira/polaris__domain-view/src/ViewAccessLevel.types.tsx';
import { useEnvironmentContainer } from '@jira/polaris__component-environment-container/src/useEnvironmentContainer.tsx';
import { useIsViewPermissionsEnabled } from '@jira/polaris__lib-entitlement-utils/src/index.tsx';
import { fg } from '@atlassian/jira-feature-gating';
import { useApplicationPermissions } from '@atlassian/jira-tenant-context-controller/src/components/application-permissions/index.tsx';
// eslint-disable-next-line jira/restricted/@atlassian/react-sweet-state
import { createHook, type BoundActions, type HookFunction } from '@atlassian/react-sweet-state';
import {
	PermissionsStore,
	usePermissions,
	usePermissionActions,
	type Actions,
} from './PermissionsContainer.tsx';
import type { State } from './State.types.tsx';
import type { LoadViewPermissionsForCurrentUserState } from './loadViewPermissionsForCurrentUser.tsx';
import { createPermissionHook, type Options } from './createPermissionHook.tsx';
import { useIsCollectionView } from './useIsCollectionView.tsx';

const useIsGlobalViewSettings: HookFunction<
	boolean,
	BoundActions<State, Actions>,
	void
> = createHook(PermissionsStore, {
	selector: (state) => state.meta.loadingProps?.isGlobalViewSettings || false,
});

const useHasManageViewsPermission = createPermissionHook('MANAGE_DISCOVERY_VIEWS');
const useHasPublishDiscoveryViewsPermission = createPermissionHook('PUBLISH_DISCOVERY_VIEWS');
const useHasPublishPublicDiscoveryViewsPermission = createPermissionHook(
	'PUBLISH_PUBLIC_DISCOVERY_VIEWS',
);
const useHasExportViewsPermission = createPermissionHook('SHARE_DISCOVERY_VIEWS');
const useHasCreateViewCommentsPermission = createPermissionHook('CREATE_DISCOVERY_VIEW_COMMENTS');
const useHasManageViewCommentsPermission = createPermissionHook('MANAGE_DISCOVERY_VIEW_COMMENTS');
const useHasPinIssueViewFieldsPermission = createPermissionHook('PIN_DISCOVERY_ISSUE_VIEW_FIELDS');

export const useCanPinIssueViewFields = (options?: Options): boolean => {
	const [hasPinIsssueViewFieldsPermission] = useHasPinIssueViewFieldsPermission(options);
	const [isCollectionView] = useIsCollectionView();
	return !isCollectionView && hasPinIsssueViewFieldsPermission;
};

export const useCanAddAndEditOwnViewComments = (options?: Options): boolean => {
	const [hasCreateViewComments] = useHasCreateViewCommentsPermission(options);
	return hasCreateViewComments;
};

export const useCanEditAllViewComments = (options?: Options): boolean => {
	const [hasManageViewCommentsPermission] = useHasManageViewCommentsPermission(options);
	return hasManageViewCommentsPermission;
};

/**
 * returns permissions related to managing a view (edit, see, access level)
 * @param containerId - view UUID or collection container ID
 * @returns
 */
export const useManageViewPermissions = (
	containerId: string | undefined,
	options?: Options,
):
	| {
			canManageView: boolean;
			canManageViews: boolean;
			canSeeView: boolean;
			// oxlint-disable-next-line @typescript-eslint/no-explicit-any
			loadViewPermissionsForCurrentUser: (...args: any[]) => void;
			viewAccessLevel?: undefined;
	  }
	| {
			canManageView: boolean;
			canManageViews: boolean;
			canSeeView: boolean;
			viewAccessLevel: ViewAccessLevel | undefined;
			loadViewPermissionsForCurrentUser: (containerId: string) => Promise<{
				role: AccessRole | undefined;
				accessLevel: ViewAccessLevel | undefined;
				state: LoadViewPermissionsForCurrentUserState;
			}>;
	  } => {
	const isViewPermissionsEnabled = useIsViewPermissionsEnabled();
	const [canManageViews] = useHasManageViewsPermission(options);
	const { loadViewPermissionsForCurrentUser } = usePermissionActions();
	const [state] = usePermissions();

	if (!isViewPermissionsEnabled) {
		return {
			canManageView: canManageViews,
			canManageViews,
			canSeeView: true,
			loadViewPermissionsForCurrentUser: noop,
		};
	}

	if (!containerId) {
		return {
			canManageView: false,
			canManageViews,
			canSeeView: true,
			loadViewPermissionsForCurrentUser,
		};
	}

	const permissionsForThisView = state.viewPermissions[containerId];

	const canManageView = canManageViews && Boolean(permissionsForThisView?.data?.canEdit);

	return {
		canManageView,
		canManageViews,
		canSeeView:
			permissionsForThisView?.loading ||
			permissionsForThisView?.data === undefined ||
			Boolean(permissionsForThisView?.data.canView),
		viewAccessLevel: permissionsForThisView?.data?.accessLevel,
		loadViewPermissionsForCurrentUser,
	};
};

export const useCanEditCollection = ({
	collectionUUID,
}: {
	collectionUUID: string | undefined;
}): boolean => {
	const { hasProductDiscoveryAccess: isLicensedUser } = useApplicationPermissions() ?? {};

	const { canManageView } = useManageViewPermissions(collectionUUID);

	return isLicensedUser && canManageView;
};

export const useLoadCollectionPermissions = (
	collectionUUID: string | undefined,
	// oxlint-disable-next-line typescript/no-invalid-void-type
): (() => void | Promise<{
	role: AccessRole | undefined;
	accessLevel: ViewAccessLevel | undefined;
	state: LoadViewPermissionsForCurrentUserState;
}>) => {
	const { loadViewPermissionsForCurrentUser } = useManageViewPermissions(collectionUUID);

	return useCallback(() => {
		if (collectionUUID) {
			return loadViewPermissionsForCurrentUser(collectionUUID);
		}
	}, [collectionUUID, loadViewPermissionsForCurrentUser]);
};

export const useCanManageViews = (options?: Options): boolean => {
	const [hasManageViewsPermission] = useHasManageViewsPermission(options);
	const [isCollectionView] = useIsCollectionView();
	const [isGlobalViewSettings] = useIsGlobalViewSettings();
	return (
		hasManageViewsPermission ||
		// temporarily give everyone manageViews permissions in a collection view
		// TODO: https://pi-dev-sandbox.atlassian.net/browse/POL-17942
		isCollectionView ||
		// On global view settings, there's no view level permissions:
		// all users who can access the global view settings, can create and edit global views
		(isGlobalViewSettings && fg('polaris_global_views'))
	);
};

export const useCanPublishDiscoveryViews = (options?: Options): boolean =>
	useHasPublishDiscoveryViewsPermission(options)[0];

export const useCanPublishPublicDiscoveryViews = (options?: Options): boolean =>
	useHasPublishPublicDiscoveryViewsPermission(options)[0];

export const useCanExportViews = (options?: Options): boolean => {
	const [hasExportViewsPermission] = useHasExportViewsPermission(options);
	const collectionId = useEnvironmentContainer()?.id ?? '';
	const [isCollectionView] = useIsCollectionView();

	const canEditCollection = useCanEditCollection({
		collectionUUID: collectionId,
	});

	return (isCollectionView && canEditCollection) || hasExportViewsPermission;
};
